import type { InAppCard } from "@/api/apps";
import type { ProjectRun } from "@/api/projects";
import { isAffdoFlow } from "@/components/submission/affdo-flow-adapter";

/**
 * Which flows belong to each in-app app family — the `target.app` a card
 * carries. A family absent here has no runs the dashboard can attribute to
 * it, so its card is chosen only by being opened (or by the name fallback).
 */
const APP_FAMILY_FLOWS: Readonly<Record<string, (flowName: string) => boolean>> = {
  affdo: isAffdoFlow,
};

/** The app family of a run's flow, or null for a tile run or a flow of no app. */
export function runAppFamily(run: Pick<ProjectRun, "flow_name">): string | null {
  const flowName = run.flow_name;
  if (flowName === null) return null;
  for (const [app, belongs] of Object.entries(APP_FAMILY_FLOWS)) {
    if (belongs(flowName)) return app;
  }
  return null;
}

/**
 * The one card the dashboard's "Start work" hero shows: the FlowStack app
 * this viewer used most recently (flowstack#1999).
 *
 * - The card they last opened (`opened`, already checked to be theirs), if
 *   `/api/apps` still lists it for them.
 * - Unless a run of another entitled card's family is newer: the newest run
 *   in `runs` (newest first, as `/api/runs` orders it) whose flow belongs to
 *   an entitled card's app. A run of no app — a plain template, a standalone
 *   tile — says nothing about which app was used, so it is passed over.
 * - With neither, the first entitled card by name. No card, no hero.
 *
 * `/api/runs` is the tenant's feed: runs record no user (flowstack#960), so
 * on a shared tenant a colleague's run counts too.
 */
export function heroCard(
  cards: readonly InAppCard[],
  opened: { slug: string; openedAt: number } | null,
  runs: readonly ProjectRun[] | undefined
): InAppCard | null {
  if (cards.length === 0) return null;
  const byName = [...cards].sort((a, b) => a.display_name.localeCompare(b.display_name));

  const openedCard = opened ? byName.find((c) => c.slug === opened.slug) : undefined;

  let ranCard: InAppCard | undefined;
  let ranAt = Number.NaN;
  for (const run of runs ?? []) {
    const family = runAppFamily(run);
    const card = family === null ? undefined : byName.find((c) => c.target.app === family);
    const at = Date.parse(run.submitted_at);
    if (card === undefined || Number.isNaN(at)) continue;
    ranCard = card;
    ranAt = at;
    break;
  }

  if (openedCard && opened) {
    return ranCard && ranAt > opened.openedAt ? ranCard : openedCard;
  }
  return ranCard ?? byName[0];
}
